import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';

import useFileReactionPicker from '../FileReactionPicker/useFileReactionPicker';
import PhotoGridViewerFooterButton from '../../grid/components/PhotoGridViewerFooterButton';
import CommentIcon from '../../icons/CommentIcon';
import ReactIcon from '../../icons/ReactIcon';
import DrawerMenu from '../DrawerMenu';
import FileComments from '../FileComments';
import FileReactionPicker from '../FileReactionPicker';
import FileReactionList from '../FileReactionList';
import useOpenModal from '../../../hooks/useOpenModal';
import { PHONE_AUTH_MODAL } from '../../../constants/modals';
import AccountSetupModal from '../../../apps/pro/hoc/RequireShutterbugAccountSetup/components/AccountSetupModal';
import useShutterbugAccountComplete from '../../../apps/pro/hoc/RequireShutterbugAccountSetup/hooks/useShutterbugAccountComplete';
import DropdownMenu from '../DropdownMenu';
import useFileComments from '../FileComments/useFileComments';
import {
  hasSocialFeaturesAccess,
  isAlbumFolderOwner,
  isAlbumOwner,
  isProAccount,
} from '../../../util/account';
import { isAlbum, isProContent } from '../../../util/album';
import { ALBUM_TYPE, FILE_COMMENT_PRIVACY } from '../../../constants/albums';

const propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string,
  }),
  entity: PropTypes.shape({
    album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
    display_public_photo_attribution: PropTypes.bool,
  }),
  isFreeWebGallery: PropTypes.bool,
};

const VideoGridViewerFooter = ({ entity, video, isFreeWebGallery }) => {
  const location = useLocation();
  const { fileCommentId, parentFileCommentId, reaction } = qs.parse(
    location.search ?? '',
  );
  const openModal = useOpenModal();
  const [commentsOpen, setCommentsOpen] = useState(!!fileCommentId);
  const [accountReactionsOpen, setAccountsReactionsOpen] = useState(!!reaction);
  const isProEntity = isProContent(entity);
  const { accountFileReaction } = useFileReactionPicker({
    videoId: video?.id,
    isProEntity,
  });
  const { commentsTotal, isOwner } = useFileComments({
    videoId: video?.id,
    isProEntity,
  });

  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const onAccountCompletedRef = useRef();
  const { user, loading, isAccountSetupComplete } =
    useShutterbugAccountComplete();
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (loading || !authenticated) {
      return;
    }

    if (isAccountSetupComplete) {
      onAccountCompletedRef.current?.();
      // Clear up once called.
      onAccountCompletedRef.current = null;
      setFormOpen(false);
    }
  }, [loading, isAccountSetupComplete, authenticated]);

  const includeSocialFeatures = hasSocialFeaturesAccess(user) && !isProEntity;

  const blockPro = isProAccount(user) && isFreeWebGallery;

  const displayCommentsAndReactions =
    !blockPro &&
    (isOwner ||
      includeSocialFeatures ||
      (video?.is_uploader && commentsTotal > 0));

  const isEntityOwner = isAlbum(entity)
    ? isAlbumOwner(user, entity)
    : isAlbumFolderOwner(user, entity);
  const isProEntityOwner = isProAccount(user) && isEntityOwner;

  return (
    <div className="video-grid-viewer-footer-wrapper">
      <div className="video-grid-viewer-footer-left" />
      <div className="video-grid-viewer-footer-center" />
      {displayCommentsAndReactions && (
        <>
          <div className="video-grid-viewer-footer-right">
            <PhotoGridViewerFooterButton
              label={`Comment (${commentsTotal})`}
              onClick={() => {
                if (!authenticated) {
                  openModal(PHONE_AUTH_MODAL);
                  onAccountCompletedRef.current = () => setCommentsOpen(true);

                  return;
                }

                if (!isAccountSetupComplete) {
                  setFormOpen(true);
                  // Set this method to be called once we confirm the account is completed and good to go.
                  onAccountCompletedRef.current = () => setCommentsOpen(true);

                  return;
                }

                setCommentsOpen(true);
              }}
              icon={<CommentIcon />}
            />
            {includeSocialFeatures && (
              <Fragment>
                {isAccountSetupComplete ? (
                  <DropdownMenu
                    placement="top-end"
                    dropdownClassName="reactions-dropdown"
                    toggle={
                      <PhotoGridViewerFooterButton
                        label="React"
                        onClick={() => {}}
                        icon={
                          accountFileReaction ? (
                            <img
                              src={
                                accountFileReaction?.system_sticker?.asset_image
                                  .url
                              }
                              alt=""
                            />
                          ) : (
                            <ReactIcon />
                          )
                        }
                      />
                    }
                  >
                    <FileReactionPicker videoId={video.id} />
                  </DropdownMenu>
                ) : (
                  <PhotoGridViewerFooterButton
                    label="React"
                    onClick={() => {
                      setFormOpen(true);
                    }}
                    icon={<ReactIcon />}
                  />
                )}
              </Fragment>
            )}
          </div>
          <DrawerMenu
            title="Comments"
            isOpen={commentsOpen}
            onClose={() => setCommentsOpen(false)}
          >
            <FileComments
              videoId={video.id}
              onGroupedReactionsClick={() => {
                if (!authenticated) {
                  openModal(PHONE_AUTH_MODAL);
                  onAccountCompletedRef.current = () =>
                    setAccountsReactionsOpen(true);

                  return;
                }

                setAccountsReactionsOpen(true);
                setCommentsOpen(false);
              }}
              target={{ fileCommentId, parentFileCommentId }}
              privateCommentingEnabled={
                (entity?.file_comment_privacy ||
                  entity?.root_album_folder?.file_comment_privacy) !==
                FILE_COMMENT_PRIVACY.PUBLIC
              }
              forceRepliesOpen={isProEntityOwner}
            />
          </DrawerMenu>
          {includeSocialFeatures && (
            <DrawerMenu
              title="Reactions"
              isOpen={accountReactionsOpen}
              onClose={() => {
                setAccountsReactionsOpen(false);
                setCommentsOpen(true);
              }}
            >
              <FileReactionList videoId={video.id} />
            </DrawerMenu>
          )}
        </>
      )}

      <AccountSetupModal
        user={user}
        forceOpen={formOpen}
        onAccountSetupComplete={() => {
          setFormOpen(false);
        }}
        onCancelAccountSetup={() => {
          onAccountCompletedRef.current = null;
          setFormOpen(false);
        }}
      />
    </div>
  );
};

VideoGridViewerFooter.propTypes = propTypes;

export default VideoGridViewerFooter;
